import { type FC, type SyntheticEvent, useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Alert,
  Button,
  IconButton,
  Tabs,
  Tab,
  useTheme,
  Chip,
  Tooltip,
  Typography,
} from "@mui/material";
import type { GridColDef } from "@mui/x-data-grid";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { ArrowLeft2 } from "iconsax-react";
import AddEditConfiguration from "./AddEditConfigurationDialog";
import Ingenieurslegende from "./Ingenieurslegende";
import {
  configurationService,
  type Rule,
} from "../../services/configurationService";
import {
  companyService,
  type CompanyDto,
} from "../../services/companyService";
import { buildBlobProxyUrl } from "../../utils/blobProxyUrl";
import ConfirmationDialog from "../../components/ConfirmationDialog";

function resolveCompanyLogoSrc(value?: string): string {
  if (!value) return "";
  if (value.startsWith("data:")) return value;
  if (value.startsWith("http") || value.startsWith("/")) return buildBlobProxyUrl(value);
  return `data:image/jpeg;base64,${value}`;
}
import CustomDataGrid from "../../components/DataGrid";
import { SeverityBadge } from "../../components/ui/Badges";
import { Icon } from "../../components/ui/icons";
import { RADIUS } from "../../theme/mixins";
import HeaderWithButton from "../../components/Header";

const Configuration: FC = () => {
  const { t } = useTranslation();
  const { companyId } = useParams<{ companyId: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  const [searchParams] = useSearchParams();

  // State management
  const [rules, setRules] = useState<Rule[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(searchParams.get("tab") === "legends" ? 1 : 0);
  const [editMode, setEditMode] = useState(false);
  const [viewMode, setViewMode] = useState(false);
  const [selectedRule, setSelectedRule] = useState<Rule | null>(null);
  const [openAddEditDialog, setOpenAddEditDialog] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [ruleToDelete, setRuleToDelete] = useState<string | null>(null);
  const [company, setCompany] = useState<CompanyDto | null>(null);

  // Load the company so the context card can show its real logo / name / domain.
  useEffect(() => {
    if (!companyId) return;
    let active = true;
    companyService
      .getById(companyId)
      .then((c) => {
        if (active) setCompany(c);
      })
      .catch((err) => console.error("Failed to load company:", err));
    return () => {
      active = false;
    };
  }, [companyId]);

  // Fetch rules
  const fetchRules = useCallback(async () => {
    if (!companyId) {
      setError(t("configuration.companyIdRequired"));
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const data = await configurationService.getByCompanyId(companyId);
      setRules(data);
    } catch (err) {
      console.error("Failed to fetch rules:", err);
      const errorMessage =
        err instanceof Error ? err.message : t("configuration.rulesLoadFailed");
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    fetchRules();
  }, [fetchRules]);

  const handleTabChange = (_event: SyntheticEvent, newValue: number) =>
    setTabValue(newValue);

  const handleOpenAdd = () => {
    setEditMode(false);
    setViewMode(false);
    setSelectedRule(null);
    setOpenAddEditDialog(true);
  };

  const handleOpenEdit = (rule: Rule) => {
    setEditMode(true);
    setViewMode(false);
    setSelectedRule(rule);
    setOpenAddEditDialog(true);
  };

  const handleOpenView = (rule: Rule) => {
    setEditMode(true);
    setViewMode(true);
    setSelectedRule(rule);
    setOpenAddEditDialog(true);
  };

  const handleDelete = async () => {
    if (!ruleToDelete || !companyId) return;

    try {
      await configurationService.delete(companyId, ruleToDelete);
      toast.success(t("configuration.ruleDeleted"));
      await fetchRules();
    } catch (err) {
      console.error("Failed to delete rule:", err);
      toast.error(t("configuration.ruleDeleteFailed"));
    } finally {
      setDeleteDialogOpen(false);
      setRuleToDelete(null);
    }
  };

  const handleDownloadRules = (rule: Rule) => {
    const jsonData = JSON.stringify(rule, null, 2);
    const blob = new Blob([jsonData], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${rule.title}-rule.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const columns: GridColDef<Rule>[] = [
    { field: "title", headerName: t("configuration.title"), minWidth: 200, flex: 1 },
    { field: "description", headerName: t("companies.description"), minWidth: 250, flex: 1 },
    {
      field: "severity",
      headerName: t("configuration.severity"),
      minWidth: 120,
      flex: 0.8,
      display: "flex",
      renderCell: (p) => <SeverityBadge value={p.row.severity} />,
    },
    { field: "category", headerName: t("configuration.category"), minWidth: 150, flex: 0.5 },
    {
      field: "view",
      headerName: t("configuration.rule"),
      flex: 0.8,
      align: "center",
      headerAlign: "center",
      sortable: false,
      display: "flex",
      renderCell: (p) => (
        <Chip
          label={t("configuration.view")}
          size="small"
          onClick={() => handleOpenView(p.row)}
          sx={{
            color: theme.palette.brand.icon,
            bgcolor: theme.palette.surface.cyan,
            border: `1px solid ${theme.palette.border.cyan}`,
            minWidth: 0,
            "&:hover": { bgcolor: theme.palette.surface.cyanHover },
          }}
        />
      ),
    },
    {
      field: "actions",
      headerName: "",
      width: 150,
      sortable: false,
      display: "flex",
      renderCell: (p) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Tooltip title={t("common.edit")}>
            <IconButton
              size="small"
              onClick={() => handleOpenEdit(p.row)}
              sx={{
                color: theme.palette.text.secondary,
                "&:hover": {
                  background: theme.palette.background.neutral,
                  color: theme.palette.brand.ocean,
                },
              }}
            >
              <Icon name="edit" size={17} />
            </IconButton>
          </Tooltip>
          <Tooltip title={t("common.download")}>
            <IconButton
              size="small"
              onClick={() => handleDownloadRules(p.row)}
              sx={{
                color: theme.palette.text.secondary,
                "&:hover": {
                  background: theme.palette.background.neutral,
                  color: theme.palette.brand.icon,
                },
              }}
            >
              <Icon name="download" size={17} />
            </IconButton>
          </Tooltip>

          {/* Destructive action reads as destructive (audit 2.4.3 / 2.7.3). */}
          <Tooltip title={t("common.delete")}>
            <IconButton
              size="small"
              onClick={() => {
                setRuleToDelete(p.row.id);
                setDeleteDialogOpen(true);
              }}
              sx={{
                color: theme.palette.error.main,
                "&:hover": {
                  background: theme.palette.status.failBg,
                  color: theme.palette.status.failFg,
                },
              }}
            >
               <Icon name="trash" size={17} />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  const header = (
    <HeaderWithButton
      icon={<ArrowLeft2 size={20} color={theme.palette.text.primary} />}
      buttonText={t("common.back")}
      onClick={() => navigate("/companies")}
      title={t("configuration.settingsTitle")}
      description={t("configuration.settingsDescription")}
      hideEndButton
    />
  );

  if (error) {
    return (
      <Box>
        {header}
        <Alert
          severity="error"
          sx={{ m: 2 }}
          action={
            <Button color="inherit" size="small" onClick={fetchRules}>
              {t("common.retry")}
            </Button>
          }
        >
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ height: "100%", minHeight: 0, display: "flex", flexDirection: "column" }}>
      {header}

      {/* company context card */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 14,
          background: theme.palette.common.white,
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 16,
          padding: "16px 20px",
          marginBottom: 20,
          boxShadow: theme.customShadows.card,
          flexShrink: 0,
        }}
      >
        {(() => {
          const logo = resolveCompanyLogoSrc(company?.logo);
          if (logo) {
            return (
              <img
                src={logo}
                alt={company?.name || t("configuration.companyFallback")}
                style={{ width: 46, height: 46, flex: "none", borderRadius: "50%", objectFit: "cover", display: "block" }}
              />
            );
          }
          return (
            <span
              style={{
                width: 46,
                height: 46,
                flex: "none",
                borderRadius: "50%",
                background: theme.palette.brand.navy,
                color: theme.palette.common.white,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 17,
                fontWeight: 700,
              }}
            >
              {company?.name?.charAt(0)?.toUpperCase() || <Icon name="building" size={22} />}
            </span>
          );
        })()}
        <div style={{ minWidth: 0 }}>
          <div
            style={{ fontSize: 16, fontWeight: 700, color: theme.palette.brand.navy }}
          >
            {company?.name || t("configuration.companyConfigFallback")}
          </div>
          <div style={{ fontSize: 13, color: theme.palette.brand.ocean }}>
            {company?.companyDomain || t("configuration.rulesForCompanyFallback")}
          </div>
        </div>
      </div>

      {/* tab */}
      <Tabs
        value={tabValue}
        onChange={handleTabChange}
        textColor="primary"
        indicatorColor="primary"
        sx={{ mb: 2.5, flexShrink: 0 }}
      >
        <Tab label={t("configuration.tabs.companyConfig")} />
        <Tab label={t("configuration.tabs.ingenieurslegende")} />
      </Tabs>

      {tabValue === 1 ? (
        <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
          <Ingenieurslegende companyId={companyId} />
        </Box>
      ) : (
        <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
          {/* title + action */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              py: 1.5,
              flexShrink: 0,
            }}
          >
            <Box>
              <Typography variant="h3" color={theme.palette.primary.dark}>
                {t("configuration.rulesTitle")}
              </Typography>
              <Typography variant="body1" color={"#6E7884"}>
                {t("configuration.rulesDescription")}
              </Typography>
            </Box>

            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={handleOpenAdd}
              startIcon={<Icon name="plus" size={17} />}
              sx={{
                borderRadius: "10px",
                textTransform: "none",
                color: theme.palette.primary.contrastText,
                bgcolor: theme.palette.primary.main,
                boxShadow: theme.customShadows.buttonCyan,
                "&:hover": { bgcolor: theme.palette.brand.luchtHover }
              }}
            >
              {t("configuration.newRule")}
            </Button>
          </Box>

          {/* rules table */}
          <Box
            sx={{
              background: theme.palette.common.white,
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: `${RADIUS.card}px`,
              boxShadow: theme.customShadows.card,
              flex: 1,
              minHeight: 0,
              width: '100%',
              position: 'relative'
            }}
          >
            <CustomDataGrid
              rows={rules}
              columns={columns}
              height="100%"
              loading={loading}
              slotProps={{
                loadingOverlay: { variant: "skeleton", noRowsVariant: "skeleton" },
              }}
              hideFooter
              disableColumnMenu
              disableRowSelectionOnClick
              getRowHeight={() => "auto"}
              sx={{
                height: "100%",
                overflowY: "auto",
                "& .MuiDataGrid-cell": {
                  padding: "8px",
                },
              }}
              slots={{
                noRowsOverlay: () => (
                  <Box
                    sx={{
                      p: "40px 22px",
                      textAlign: "center",
                      color: theme.palette.text.secondary,
                      fontSize: 14,
                    }}
                  >
                    {t("configuration.noRules")}
                  </Box>
                ),
              }}
            />
          </Box>
        </Box>
      )}

      {/* Add/Edit Dialog */}
      <AddEditConfiguration
        open={openAddEditDialog}
        onClose={() => {
          setOpenAddEditDialog(false);
          setEditMode(false);
          setViewMode(false);
          setSelectedRule(null);
        }}
        editMode={editMode}
        viewMode={viewMode}
        companyId={companyId || ""}
        initialData={
          selectedRule
            ? {
                id: selectedRule.id,
                title: selectedRule.title,
                description: selectedRule.description,
                severity: selectedRule.severity,
                category: selectedRule.category,
                expected: selectedRule.expected,
                detect: selectedRule.detect,
                preferred_clause: selectedRule.preferred_clause,
                metadata: selectedRule.metadata,
              }
            : undefined
        }
        onSave={fetchRules}
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        open={deleteDialogOpen}
        title={t("configuration.deleteRuleTitle")}
        message={t("configuration.deleteRuleMessage")}
        confirmText={t("common.delete")}
        cancelText={t("common.cancel")}
        confirmColor="error"
        onConfirm={handleDelete}
        onCancel={() => {
          setDeleteDialogOpen(false);
          setRuleToDelete(null);
        }}
      />
    </Box>
  );
};

export default Configuration;
